class FactsChart extends HTMLElement {
  #observer = null;
  #resizeObserver = null;
  #counters = [];

  connectedCallback() {
    this.#counters = [...this.querySelectorAll('.facts__value')].map((element) => ({
      element,
      target: parseInt(element.textContent, 10) || 0,
    }));

    this.#publishShapeMetrics();

    if (!this.prefersReducedMotion) {
      for (const counter of this.#counters) counter.element.textContent = '0%';
    }

    this.#resizeObserver = new ResizeObserver(() => this.#publishShapeMetrics());
    this.#resizeObserver.observe(this);

    if (this.prefersReducedMotion) {
      this.reveal();
      return;
    }

    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.25 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #publishShapeMetrics() {
    for (const item of this.querySelectorAll('.facts__item')) {
      const shapes = item.querySelector('.facts__shapes');
      if (!shapes) continue;

      const pill = shapes.querySelector('.facts__value');
      const counter = this.#counters.find((entry) => entry.element === pill);
      const shownText = counter ? pill.textContent : null;

      if (counter) {
        item.style.setProperty('--facts-value-width', 'auto');
        pill.textContent = `${counter.target}%`;
      }

      const barHeight = parseFloat(getComputedStyle(item).getPropertyValue('--facts-bar-height')) || 0;
      const spacer = shapes.querySelector('.facts__fill-spacer');
      const fillMin = Math.max(barHeight, spacer ? spacer.offsetWidth : 0);

      let rest = 0;
      for (const part of shapes.querySelectorAll('.facts__drop, .facts__value')) {
        rest += parseFloat(getComputedStyle(part).marginLeft) + part.offsetWidth;
      }

      if (counter) {
        this.#setLength(item, '--facts-value-width', pill.offsetWidth);
        pill.textContent = shownText;
      }

      this.#setLength(item, '--facts-shape-rest', rest);
      this.#setLength(item, '--facts-shape-end', fillMin + rest);
    }
  }

  #setLength(item, name, value) {
    const next = `${Math.round(value)}px`;
    if (item.style.getPropertyValue(name) === next) return;

    item.style.setProperty(name, next);
  }

  #countUp() {
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / 1200, 1);
      const eased = 1 - (1 - progress) ** 3;

      for (const { element, target } of this.#counters) {
        element.textContent = `${Math.round(target * eased)}%`;
      }

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }

  #handleIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.#observer?.disconnect();
    this.#observer = null;
    this.reveal();
  };

  reveal() {
    if (this.prefersReducedMotion) {
      this.classList.add('facts--revealed');
      return;
    }

    requestAnimationFrame(() => {
      this.classList.add('facts--revealed');
      this.#countUp();
    });
  }
}

if (!customElements.get('facts-chart')) {
  customElements.define('facts-chart', FactsChart);
}
